import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getNativePlatform,
  type NativePlatform,
} from '#features/notifications/utils/nativePlatform';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import {
  BANNER_DISMISS_MS,
  bannerDismissKey,
  RECHECK_MIN_INTERVAL_MS,
  SERVER_CLOSED_RECHECK_MS,
} from '../constants';
import type { UpdateDecision } from '../model/types';
import { fetchAppStatus, MAINTENANCE_OFF } from '../utils/appStatus';
import { getAppBuild, getCachedBuild, refreshAppBuild } from '../utils/appVersion';
import { setBuildOutdated } from '../utils/outdatedState';
import {
  setReviewPromptEnabled,
  setServerClosedListener,
  setUpdatePromptVisible,
} from '../utils/statusFlags';
import { decideUpdate, isBuildOutdated } from '../utils/updateDecision';

const NONE: UpdateDecision = { kind: 'none' };

const readDismissedAt = (target: number): number | null => {
  const raw = localStorage.getItem(bannerDismissKey(target));
  if (!raw) {
    return null;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Решает, показывать ли баннер обновления или экран блокировки.
 *
 * Проверка идёт при старте и при каждом возвращении приложения из фона — не реже, чем раз в
 * полчаса. Только холодного старта мало: приложение живёт открытым сутками, а рубильник надо
 * уметь поднять в течение инцидента, а не к следующему запуску.
 *
 * Ничего не блокирует и не задерживает: проверка асинхронная, до её завершения решение — «none».
 */
export const useAppUpdateGate = () => {
  const [decision, setDecision] = useState<UpdateDecision>(NONE);
  /**
   * Работы едут тем же ответом, что и пороги версий, и живут в этом же хуке намеренно: второй
   * хук означал бы второй запрос того же файла на каждой проверке, а экран человеку всё равно
   * показывается один.
   */
  const [maintenance, setMaintenance] = useState(MAINTENANCE_OFF);
  const lastCheckedAt = useRef(0);
  /** Отдельная отметка: внеочередные проверки по сигналу шлюза считаются своим счётом. */
  const lastServerClosedAt = useRef(0);
  const platformRef = useRef<NativePlatform>('web');

  const check = useCallback(async (force: boolean) => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    const startedAt = Date.now();
    if (!force && startedAt - lastCheckedAt.current < RECHECK_MIN_INTERVAL_MS) {
      return;
    }
    lastCheckedAt.current = startedAt;

    const [build, platform, status] = await Promise.all([
      getAppBuild(),
      getNativePlatform(),
      fetchAppStatus(),
    ]);
    platformRef.current = platform;

    // Флаг для текстов ошибок держим в актуальном состоянии независимо от того, показываем
    // мы что-то или нет: баннер может быть закрыт человеком, а причина отказа шлюза от этого
    // не меняется.
    setBuildOutdated(isBuildOutdated({ build, platform, status }));
    // Рубильник карточки оценки едет тем же файлом: другого канала, переживающего выкат,
    // у приложения нет. Недоступный конфиг её не выключает — «не знаю» здесь тоже fail-open.
    setReviewPromptEnabled(status?.reviewPrompt ?? true);
    // Не смогли прочитать — значит работаем. Недоступный конфиг не должен уметь остановить
    // приложение: цена ложной остановки выше цены пропущенной.
    const nextMaintenance = status?.maintenance ?? MAINTENANCE_OFF;
    setMaintenance(nextMaintenance);

    const preliminary = decideUpdate({
      build,
      platform,
      status,
      dismissedAt: null,
      now: startedAt,
      dismissWindowMs: BANNER_DISMISS_MS,
    });

    // Отметку о закрытии читаем уже зная целевую версию: ключ привязан к ней, а не к текущей
    // сборке — иначе после обновления баннер вернулся бы к тому, кто только что обновился.
    const next =
      preliminary.kind === 'banner'
        ? decideUpdate({
            build,
            platform,
            status,
            dismissedAt: readDismissedAt(preliminary.target),
            now: startedAt,
            dismissWindowMs: BANNER_DISMISS_MS,
          })
        : preliminary;

    // Экран занят — это должно быть видно тем, кто тоже собирается заговорить с человеком
    // (карточка оценки Play, чек-лист разрешений). Два окна подряд человек закроет не глядя,
    // оба. Работы считаются наравне с обновлением: экран там такой же полноэкранный, и просить
    // на нём разрешения на уведомления — значит просить в момент, когда продукт не работает.
    setUpdatePromptVisible(nextMaintenance.enabled || next.kind !== 'none');
    setDecision(next);
  }, []);

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    void check(true);

    let remove: (() => void) | undefined;
    let active = true;

    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) {
            return;
          }
          void (async () => {
            /*
             * Сначала перечитываем номер сборки, и только потом решаем, обязательна ли проверка.
             *
             * Приложение может пережить собственное обновление: Android заменяет APK, а процесс
             * не убивает. Человек уходит в Play, обновляется, возвращается — и видит баннер,
             * зовущий на версию, которая у него уже стоит; кнопка ведёт в магазин, где написано
             * «Открыть». Поймано на живом телефоне.
             *
             * Обычной проверки здесь мало: она пропускается получасовым интервалом, то есть
             * баннер висел бы ещё полчаса. Сменился номер сборки — значит проверка внеочередная
             * и обязательная, и баннер уходит в тот же миг, когда человек вернулся в приложение.
             */
            const before = getCachedBuild();
            const after = await refreshAppBuild();
            void check(after !== before);
          })();
        });
        if (!active) {
          void handle.remove();
          return;
        }
        remove = () => void handle.remove();
      } catch {
        // Слушателя нет — останется проверка на старте. Не повод падать.
      }
    })();

    return () => {
      active = false;
      remove?.();
    };
  }, [check]);

  /**
   * Шлюз сказал «сервер закрыт». Идём за статусом вне очереди — но не чаще, чем раз в
   * {@link SERVER_CLOSED_RECHECK_MS}: 503 приходит на каждый запрос, а их на экране десятки,
   * и без порога один выкат превратился бы в очередь одинаковых проверок.
   *
   * Решение всё равно принимает статус, а не этот сигнал: 503 бывает и от чужой поломки, и
   * показывать по нему заглушку означало бы врать про работы, которых нет.
   */
  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    setServerClosedListener(() => {
      const at = Date.now();
      if (at - lastServerClosedAt.current < SERVER_CLOSED_RECHECK_MS) {
        return;
      }
      lastServerClosedAt.current = at;
      void check(true);
    });

    return () => setServerClosedListener(null);
  }, [check]);

  const dismiss = useCallback(() => {
    // Снаружи апдейтера: в StrictMode он вызывается дважды, и побочным эффектам там не место.
    setUpdatePromptVisible(false);
    setDecision((current) => {
      if (current.kind === 'banner') {
        localStorage.setItem(bannerDismissKey(current.target), String(Date.now()));
      }
      return NONE;
    });
  }, []);

  /**
   * Внеочередная проверка. Нужна экрану работ: он висит на месте, пока идёт выкат, и обязан
   * уйти сам — обычный получасовой интервал означал бы, что человек сидит перед заглушкой
   * ещё полчаса после того, как всё починили.
   */
  const recheck = useCallback(() => void check(true), [check]);

  return { decision, maintenance, platform: platformRef.current, dismiss, recheck };
};
